/**
 * Platform Admin Login Page
 *
 * Command Center aesthetic - dark, technical, infrastructure-focused.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { platformLogin } from '@/lib/platform-api';
import { usePlatformAuthStore } from '@/stores/platform-auth';

export function PlatformLogin() {
  const navigate = useNavigate();
  const { setAdmin, setAccessToken, isAuthenticated, setLoading, setError } =
    usePlatformAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setLocalError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/platform/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setLocalError(null);
    setError(null);

    try {
      const response = await platformLogin(email, password);

      if (response.data) {
        setAccessToken(response.data.accessToken);
        setAdmin({
          id: response.data.admin.id,
          email: response.data.admin.email,
          name: response.data.admin.name,
          organizationId: response.data.admin.organizationId,
          isSuperAdmin: response.data.admin.isSuperAdmin,
          isPrimary: response.data.admin.isPrimary,
        });
        setLoading(false);
        navigate('/platform/dashboard', { replace: true });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setLocalError(message);
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="platform-login">
      {/* Animated background grid */}
      <div className="platform-login__grid" />

      {/* Gradient overlay */}
      <div className="platform-login__overlay" />

      {/* Content */}
      <div className="platform-login__content">
        {/* Header */}
        <div className="platform-login__header">
          <div className="platform-login__logo">
            <svg viewBox="0 0 40 40" className="platform-login__logo-icon">
              <rect x="4" y="4" width="14" height="14" fill="currentColor" opacity="0.9" />
              <rect x="22" y="4" width="14" height="14" fill="currentColor" opacity="0.6" />
              <rect x="4" y="22" width="14" height="14" fill="currentColor" opacity="0.6" />
              <rect x="22" y="22" width="14" height="14" fill="currentColor" opacity="0.3" />
            </svg>
            <span className="platform-login__logo-text">PREGOO</span>
          </div>
          <div className="platform-login__badge">CONTROL PLANE</div>
        </div>

        {/* Login card */}
        <div className="platform-login__card">
          <div className="platform-login__card-header">
            <h1 className="platform-login__title">Platform Access</h1>
            <p className="platform-login__subtitle">
              Authenticate to manage organizations and platform settings
            </p>
          </div>

          <form onSubmit={handleSubmit} className="platform-login__form">
            {error && (
              <div className="platform-login__error">
                <svg className="platform-login__error-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{error}</span>
              </div>
            )}

            <div className="platform-login__field">
              <label className="platform-login__label">
                <span className="platform-login__label-text">EMAIL</span>
                <span className="platform-login__label-dot" />
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="platform-login__input"
                placeholder="admin@example.com"
                autoComplete="email"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="platform-login__field">
              <label className="platform-login__label">
                <span className="platform-login__label-text">PASSWORD</span>
                <span className="platform-login__label-dot" />
              </label>
              <div className="platform-login__input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="platform-login__input"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="platform-login__toggle-password"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !email || !password}
              className="platform-login__submit"
            >
              {isSubmitting ? (
                <>
                  <svg className="platform-login__spinner" viewBox="0 0 24 24">
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="3"
                      fill="none"
                      strokeDasharray="31.4 31.4"
                    />
                  </svg>
                  <span>AUTHENTICATING...</span>
                </>
              ) : (
                <>
                  <span>AUTHENTICATE</span>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="platform-login__arrow"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </button>
          </form>

          <div className="platform-login__footer">
            <span className="platform-login__footer-text">Platform administrators only</span>
          </div>
        </div>

        {/* Status indicators */}
        <div className="platform-login__status">
          <div className="platform-login__status-item">
            <span className="platform-login__status-dot platform-login__status-dot--active" />
            <span>Systems Online</span>
          </div>
          <div className="platform-login__status-item">
            <span className="platform-login__status-dot platform-login__status-dot--active" />
            <span>API Connected</span>
          </div>
        </div>
      </div>

      <style>{`
        .platform-login {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
          background: #0A0F1C;
        }

        .platform-login__grid {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(0, 229, 255, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 229, 255, 0.03) 1px, transparent 1px);
          background-size: 50px 50px;
          animation: gridMove 20s linear infinite;
        }

        @keyframes gridMove {
          0% { transform: translate(0, 0); }
          100% { transform: translate(50px, 50px); }
        }

        .platform-login__overlay {
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse at 50% 0%, rgba(0, 229, 255, 0.08) 0%, transparent 60%),
                      radial-gradient(ellipse at 100% 100%, rgba(139, 92, 246, 0.05) 0%, transparent 50%);
        }

        .platform-login__content {
          position: relative;
          z-index: 10;
          width: 100%;
          max-width: 420px;
          padding: 24px;
        }

        .platform-login__header {
          text-align: center;
          margin-bottom: 32px;
        }

        .platform-login__logo {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-bottom: 16px;
        }

        .platform-login__logo-icon {
          width: 40px;
          height: 40px;
          color: #00E5FF;
        }

        .platform-login__logo-text {
          font-family: 'JetBrains Mono', monospace;
          font-size: 24px;
          font-weight: 700;
          letter-spacing: 4px;
          color: #ffffff;
        }

        .platform-login__badge {
          display: inline-block;
          font-family: 'JetBrains Mono', monospace;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 2px;
          color: #00E5FF;
          background: rgba(0, 229, 255, 0.1);
          border: 1px solid rgba(0, 229, 255, 0.3);
          padding: 6px 16px;
          border-radius: 2px;
        }

        .platform-login__card {
          background: rgba(15, 23, 42, 0.8);
          border: 1px solid rgba(0, 229, 255, 0.15);
          border-radius: 4px;
          backdrop-filter: blur(20px);
          overflow: hidden;
        }

        .platform-login__card-header {
          padding: 32px 32px 24px;
          border-bottom: 1px solid rgba(0, 229, 255, 0.1);
        }

        .platform-login__title {
          font-family: 'DM Sans', sans-serif;
          font-size: 24px;
          font-weight: 600;
          color: #ffffff;
          margin: 0 0 8px;
        }

        .platform-login__subtitle {
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          color: rgba(255, 255, 255, 0.5);
          margin: 0;
        }

        .platform-login__form {
          padding: 24px 32px 32px;
        }

        .platform-login__error {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 4px;
          margin-bottom: 20px;
          color: #f87171;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
        }

        .platform-login__error-icon {
          width: 18px;
          height: 18px;
          flex-shrink: 0;
        }

        .platform-login__field {
          margin-bottom: 20px;
        }

        .platform-login__label {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 8px;
        }

        .platform-login__label-text {
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 1.5px;
          color: rgba(255, 255, 255, 0.6);
        }

        .platform-login__label-dot {
          width: 4px;
          height: 4px;
          background: #00E5FF;
          border-radius: 50%;
        }

        .platform-login__input-wrapper {
          position: relative;
        }

        .platform-login__input {
          width: 100%;
          padding: 14px 16px;
          font-family: 'DM Sans', sans-serif;
          font-size: 15px;
          color: #ffffff;
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 4px;
          outline: none;
          transition: all 0.2s ease;
        }

        .platform-login__input::placeholder {
          color: rgba(255, 255, 255, 0.3);
        }

        .platform-login__input:focus {
          border-color: #00E5FF;
          box-shadow: 0 0 0 3px rgba(0, 229, 255, 0.1);
        }

        .platform-login__input:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .platform-login__toggle-password {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          padding: 4px;
          background: none;
          border: none;
          color: rgba(255, 255, 255, 0.4);
          cursor: pointer;
          transition: color 0.2s ease;
        }

        .platform-login__toggle-password:hover {
          color: rgba(255, 255, 255, 0.7);
        }

        .platform-login__toggle-password svg {
          width: 20px;
          height: 20px;
        }

        .platform-login__submit {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 16px 24px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 1.5px;
          color: #0A0F1C;
          background: linear-gradient(135deg, #00E5FF 0%, #00B8D4 100%);
          border: none;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-top: 8px;
        }

        .platform-login__submit:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 20px rgba(0, 229, 255, 0.4);
        }

        .platform-login__submit:active:not(:disabled) {
          transform: translateY(0);
        }

        .platform-login__submit:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .platform-login__spinner {
          width: 18px;
          height: 18px;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          100% { transform: rotate(360deg); }
        }

        .platform-login__arrow {
          width: 18px;
          height: 18px;
          transition: transform 0.2s ease;
        }

        .platform-login__submit:hover:not(:disabled) .platform-login__arrow {
          transform: translateX(4px);
        }

        .platform-login__footer {
          padding: 16px 32px;
          border-top: 1px solid rgba(0, 229, 255, 0.1);
          text-align: center;
        }

        .platform-login__footer-text {
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px;
          color: rgba(255, 255, 255, 0.3);
          letter-spacing: 0.5px;
        }

        .platform-login__status {
          display: flex;
          justify-content: center;
          gap: 24px;
          margin-top: 32px;
        }

        .platform-login__status-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px;
          color: rgba(255, 255, 255, 0.4);
        }

        .platform-login__status-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.3);
        }

        .platform-login__status-dot--active {
          background: #10B981;
          box-shadow: 0 0 8px rgba(16, 185, 129, 0.6);
          animation: pulse 2s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        @media (max-width: 480px) {
          .platform-login__content {
            padding: 16px;
          }

          .platform-login__card-header,
          .platform-login__form,
          .platform-login__footer {
            padding-left: 20px;
            padding-right: 20px;
          }

          .platform-login__status {
            flex-direction: column;
            align-items: center;
            gap: 12px;
          }
        }
      `}</style>
    </div>
  );
}

export default PlatformLogin;
